
import Vue from 'vue';
import Vuelidate from 'vuelidate';
import FormInput from '@customer/components/form/FormInput.vue';
import FormCheckbox from '@customer/components/form/FormCheckbox.vue';
import BaseButton from '@customer/components/base/BaseButton.vue';

import SocialMediaForm from './SocialMediaForm.vue';

import checkConfig from '@customer/config/check.config';
import loginConfig from '@customer/config/login.config';
import signupConfig from '@customer/config/signup.config';
import recoverPasswordConfig from '@customer/config/recoverPassword.config';
import { TranslateResult } from 'vue-i18n';
import { FormBuilder } from './utils/FormBuilder';
import { FormBuilderConfig, FormType } from '@customer/types/formBuilder';

import { getPortalLoginSettings } from '@customer/utils/initSettings';

const isServer = typeof window === 'undefined';

if (!Vue.prototype.$VuelidateInstalled && !isServer) {
  Vue.prototype.$VuelidateInstalled = true;
  // @ts-ignore
  Vue.use(Vuelidate);
}

const formBuilder = new FormBuilder({
  check: checkConfig,
  login: loginConfig,
  signup: signupConfig,
  recoverPassword: recoverPasswordConfig,
});

const formConfig = { ...formBuilder.config };

type FormBuilderData = FormBuilderConfig['data'];

interface Data extends FormBuilderData {
  validationEnabled: boolean;
  submitStatus: 'default' | 'error' | 'pending' | 'success';
  formErrorMessage: TranslateResult | null;
  loginTokenCreated: boolean;
}

interface Methods {
  submit: (type: FormType) => void;
  resetForm: () => void;
  chooseFormType: (type: FormType) => void;
  getInputErrorMessage: (input: any) => TranslateResult | null;
  handleInputMask: (input: any) => void;
  handleLoginWithToken: () => void;
  handleSocialError: (error: string) => void;
}

interface Computed {
  isCheckForm: boolean;
  isLoginForm: boolean;
  isSignupForm: boolean;
  formHtml: FormBuilderConfig['html'][FormType]['default'];
  formInputs: FormBuilderConfig['html'][FormType]['input'];
  enableSocialLogin: boolean;
  formHtmlStatus: string;
  storedFormType: FormType;
}

interface Props {
  hideSignup: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    FormInput,
    FormCheckbox,
    BaseButton,
    SocialMediaForm,
  },
  props: {
    hideSignup: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      ...formConfig.data,
      validationEnabled: false,
      submitStatus: 'default',
      formErrorMessage: null,
      loginTokenCreated: false,
    };
  },
  validations: formConfig.validations,
  computed: {
    isCheckForm() {
      return this.storedFormType === 'check';
    },
    isLoginForm() {
      return this.storedFormType === 'login';
    },
    isSignupForm() {
      return this.storedFormType === 'signup';
    },
    formHtmlStatus() {
      const html = formConfig.html[this.storedFormType];
      const status = this.submitStatus as 'default';
      return html[status] ? status : 'default';
    },
    formHtml() {
      const html = formConfig.html[this.storedFormType];
      return html[this.formHtmlStatus as 'default'];
    },
    formInputs() {
      return formConfig.html[this.storedFormType].input;
    },
    storedFormType() {
      return this.$store.state.customer.loginFormType || this.formType;
    },
    enableSocialLogin() {
      return getPortalLoginSettings('enableSocialLogin') && this.storedFormType === 'check';
    },
  },
  destroyed() {
    this.resetForm();
  },
  methods: {
    async submit(type) {
      const form = this.$v[type];
      this.validationEnabled = true;
      form.$touch();

      if (this.submitStatus === 'pending' || form.$invalid) {
        this.submitStatus = form.$invalid ? 'error' : 'pending';
        return;
      }

      this.submitStatus = 'pending';

      try {
        const formData = { ...form.$model };

        if (formData.repeatPassword) {
          delete formData.repeatPassword;
          delete formData.agreeTerms;
        }

        const response = await this.$store.dispatch(`customer/${type}`, formData);

        if (type === 'check') {
          this.submitStatus = 'default';
          const formType = !response ? 'login' : 'signup';
          this.$store.commit('customer/setLoginFormType', formType);
          this.$v[formType].email!.$model = formData.email;
        } else {
          this.submitStatus = 'success';
        }
      } catch (error) {
        this.formErrorMessage = this.$te(`portal_login.error.${error.message}`)
          ? this.$t(`portal_login.error.${error.message}`)
          : this.$t(`portal_login.error.${error.unknown}`);
        this.submitStatus = 'default';
      }
    },
    resetForm() {
      this.submitStatus = 'default';
      this.formErrorMessage = null;
      this.validationEnabled = false;
      this.$v.$reset();
      formBuilder.resetData(this.$data[this.storedFormType]);
      this.$store.commit('customer/setLoginFormType', 'check');
    },
    chooseFormType(type) {
      this.resetForm();
      this.formType = type;
      this.$store.commit('customer/setLoginFormType', type);
    },
    getInputErrorMessage(input) {
      const keys = Object.keys(input.$params);
      let errorMessage: TranslateResult | null = null;

      for (const key of keys) {
        if (input[key] === false) {
          errorMessage = this.$t(`portal_login.error.${key}`, { value: input.$params?.minLength?.min });
          break;
        }
      }

      return errorMessage;
    },
    handleInputMask(ref) {
      const element: any = this.$refs[ref];
      const input = element[0].$el;
      input.type = input.type === 'password' ? 'text' : 'password';
    },
    async handleLoginWithToken() {
      const form = this.$v['login'];
      const formData = { ...form.$model };
      const success = await this.$store.dispatch('customer/createLoginToken', formData);
      if (success) {
        this.loginTokenCreated = true;
      }
    },
    handleSocialError(error) {
      this.formErrorMessage = this.$t(`portal_login.error.${error}`);
    },
  },
});
